/**
 * The roster picker's search (D59 §2).
 *
 * **This ranks; it does not decide.** A matcher that is wrong admits the wrong
 * person. A ranker that is wrong puts the right row second, and the guest taps
 * it anyway — then confirms it by name on the next screen. That is the whole
 * reason fuzziness is safe here, and it is why nothing in this file is allowed
 * to become an authority on identity.
 *
 * 159 names against a handful of tokens is microseconds on a phone. No index,
 * no worker, no library.
 */
import type { RosterEntry } from '@mv/contracts';

/** NFKD, combining marks stripped, lowercased, punctuation dropped. `José`
 *  and `Jose` have to be the same string before anything else can compare. */
export const normalise = (s: string) => s
  .normalize('NFKD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const tokens = (s: string) => normalise(s).split(' ').filter(Boolean);

/**
 * Damerau-Levenshtein, bounded.
 *
 * The transposition case is the reason it is not plain Levenshtein: `Parkeh`
 * for `Parekh` is one slip of two adjacent fingers, and counting it as two
 * edits puts the guest's own surname below strangers'. Bounded because past the
 * limit the exact distance is not interesting — only that it is too far.
 */
export function distance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev2: number[] = [];
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        v = Math.min(v, prev2[j - 2] + 1);
      }
      row.push(v);
      if (v < best) best = v;
    }
    if (best > max) return max + 1;
    prev2 = prev; prev = row;
  }
  return prev[b.length];
}

/**
 * How well one typed token matches one name token.
 *
 * A clean prefix always outranks a typo — `pri` for `Priti` is somebody typing
 * carefully and stopping, while `prizi` is somebody getting it wrong, and the
 * first deserves to sort higher. That is why the prefix band starts at 60 and
 * is only *scaled* by how much of the name it covers, rather than being the
 * raw proportion: a bare proportion would put a short prefix below an edit.
 *
 * The length floors on the edit bands matter for the same reason in reverse.
 * At three characters an edit of one reaches most of the alphabet, so allowing
 * it would make every short query match everything.
 */
export function scoreToken(q: string, n: string): number {
  if (q === n) return 100;
  if (n.startsWith(q)) return 60 + Math.round(20 * (q.length / n.length));
  if (n.length >= 4 && q.length >= 3 && distance(q, n, 1) <= 1) return 50;
  if (n.length >= 6 && q.length >= 4 && distance(q, n, 2) <= 2) return 30;
  return 0;
}

/**
 * A whole name against a whole query.
 *
 * **Every query token must match something**, so `videet parekh` does not match
 * `Videet Shah` merely because half of it does. Token order is free, because
 * the guest list was supplied `Last,First` and some guests will type it the way
 * they last saw it written.
 *
 * **A first-name hit outranks a surname hit.** There are 26 Shahs on this list;
 * without the bonus, somebody typing their own first name is buried under a
 * surname cluster they are not looking for.
 */
const FIRST_NAME_BONUS = 25;

export function scoreName(query: string, fullName: string): number {
  const qs = tokens(query);
  const ns = tokens(fullName);
  if (!qs.length || !ns.length) return 0;

  let total = 0;
  for (const q of qs) {
    let best = 0;
    for (let i = 0; i < ns.length; i++) {
      const s = scoreToken(q, ns[i]);
      if (s === 0) continue;
      const weighted = s + (i === 0 ? FIRST_NAME_BONUS : 0);
      if (weighted > best) best = weighted;
    }
    if (best === 0) return 0;   // this token matched nothing — the name is out
    total += best;
  }
  return total / qs.length;
}

/**
 * The floor. Below this a row is not offered at all, so a query that means
 * nothing returns nothing rather than all 159 rows sorted by noise — which is
 * the state where a guest taps whatever is at the top.
 */
export const MIN_SCORE = 45;

/** The picker's list. Ties break alphabetically so the order never wobbles
 *  between keystrokes, which is its own kind of wrong on a phone. */
export function search(entries: RosterEntry[], query: string, limit = 8): RosterEntry[] {
  if (normalise(query).length < 2) return [];
  return entries
    .map((e) => ({ e, score: scoreName(query, e.fullName) }))
    .filter((r) => r.score >= MIN_SCORE)
    .sort((a, b) => b.score - a.score || a.e.fullName.localeCompare(b.e.fullName))
    .slice(0, limit)
    .map((r) => r.e);
}
